import React, { useEffect, useRef } from "react";
import QrCreator from "qr-creator";
import { Clock, Lightbulb } from "lucide-react";
import PositionYourPhone from "../assets/mobile-webcam/PositionYourPhone.mp4";
import SameWifiNetwork from "../assets/mobile-webcam/SameWifiNetwork.svg";

interface MobileWebCamBeforeConnectionProps {
  connectionUrl: string;
}

const MobileWebCamBeforeConnection: React.FC<MobileWebCamBeforeConnectionProps> = ({ connectionUrl }) => {
  const qrCodeRef = useRef<HTMLDivElement>(null);

  const slide = {
    video: PositionYourPhone,
    title: "Workspace Monitoring",
    description: "Your phone will act as a workspace monitor to ensure interview integrity.",
    details: ["Your laptop screen", "Your hands on keyboard"],
    infoBox: {
      title: "Why is this needed?",
      content:
        "This ensures fair evaluation for everyone, builds trust with employers, and protects the credibility of your verified skills.",
    },
  };

  useEffect(() => {
    if (!qrCodeRef.current) return;

    qrCodeRef.current.innerHTML = "";
    QrCreator.render(
      {
        text: connectionUrl,
        radius: 0.5,
        ecLevel: "H",
        fill: "#000000",
        background: "#ffffff",
        size: 200,
      },
      qrCodeRef.current,
    );
  }, [connectionUrl]);

  return (
    <div className="flex flex-row gap-6 w-full items-start">
        <div className="flex-[6.5] relative">
          <div className="overflow-hidden rounded-xl">
            <div className="flex flex-col min-h-[600px] bg-white rounded-xl border border-[#0000001A] p-8 gap-8 relative flex-shrink-0">
              <div className="w-full h-[260px] bg-[#F5F5F5] rounded-md flex items-center justify-center overflow-hidden flex-shrink-0">
                <video
                  src={slide.video}
                  className="w-full h-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              </div>

              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-5">
                  <div className="flex flex-col gap-[2px]">
                    <h3 className="text-[18px] font-ubuntu-sans font-medium text-[#414447] leading-[27px]">
                      {slide.title}
                    </h3>
                    <p className="text-sm text-[#686B6D] leading-[21px]">
                      {slide.description}
                    </p>
                  </div>
                </div>

                {slide.details && (
                  <div className="flex flex-col gap-[10px]">
                    <p className="text-sm leading-[21px] text-[#202326]">It captures:</p>
                    <div className="flex flex-col gap-[5px]">
                      {slide.details.map((detail) => (
                        <p key={detail} className="text-sm leading-[21px] text-[#686B6D]">
                          {detail}
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                {slide.infoBox && (
                  <div className="flex gap-3 p-4 bg-[#EFF6FF] border border-[#BEDBFF] rounded-[10px]">
                    <div className="flex flex-col gap-2 flex-1">
                      {slide.infoBox.title && (
                        <div className="flex items-center gap-2">
                          <Lightbulb className="w-5 h-5 text-[#1559FC] flex-shrink-0" />
                          <p className="text-base text-[#202326] leading-[24px] font-medium">
                            {slide.infoBox.title}
                          </p>
                        </div>
                      )}
                      <p className="text-sm text-[#68696B] leading-[21px]">
                        {slide.infoBox.content}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex-[3.5] flex flex-col gap-5">
          <div className="flex flex-col bg-white rounded-[10px] border-2 border-[#E5EAEC] py-8 px-6 gap-7">
            <img
              src={SameWifiNetwork}
              alt="Same Wifi Network"
              className="w-full h-full px-8"
            />

            <p className="text-base text-[#414447] leading-[24px] font-normal text-center">
              Make sure both devices are on the{" "}
              <span className="font-semibold text-[#0C0F12]">Same WiFi</span>{" "}
              network
            </p>
          </div>

          <div className="flex flex-col items-center justify-center bg-white rounded-[10px] border-2 border-[#E5EAEC] py-8 px-[50px]">
            <div
              ref={qrCodeRef}
              className="w-[200px] h-[200px] bg-white"
              aria-label="QR Code for mobile camera setup"
            />
            <p className="mt-6 text-base font-ubuntu-sans text-[#001630] font-medium text-center leading-[24px]">
              Scan with Your Phone
            </p>
          </div>

          <div className="flex items-center gap-3 px-[17px] py-[17px] bg-[#FFF8EA] border border-[#FEC057] rounded-[10px]">
            <Clock className="w-5 h-5 text-[#D48A0C] flex-shrink-0" />
            <span className="text-sm text-[#D48A0C] leading-[24px]">
              Waiting for mobile device...
            </span>
          </div>
        </div>
    </div>
  );
};

export default MobileWebCamBeforeConnection;

